import React, { useCallback, useRef, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { 
  Bold, 
  Italic, 
  Underline as UnderlineIcon, 
  Strikethrough, 
  List, 
  ListOrdered, 
  Quote, 
  Code,
  Link as LinkIcon,
  Image as ImageIcon,
  Undo,
  Redo,
  Minus
} from 'lucide-react';
import { fileAPI } from '../lib/api';
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';

const MenuButton = ({ onClick, isActive, disabled, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`p-1.5 rounded hover:bg-brand-surface transition-colors ${
      isActive ? 'bg-brand-surface text-brand-primary' : 'text-brand-text-secondary'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
  >
    {children}
  </button>
);

const MenuBar = ({ editor, onImageUpload }) => {
  const fileInputRef = useRef(null);

  if (!editor) return null;

  const addLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);
    
    if (url === null) return;
    
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check if it's an image
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    try {
      await onImageUpload(file);
    } catch (error) {
      console.error('Failed to upload image:', error);
      alert('Failed to upload image');
    }
    
    // Reset input
    e.target.value = '';
  };

  return (
    <div className="flex flex-wrap items-center gap-0.5 p-2 border-b border-brand-border bg-brand-surface/50 rounded-t-lg">
      <MenuButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        isActive={editor.isActive('bold')}
        title="Bold (Ctrl+B)"
      >
        <Bold className="w-4 h-4" />
      </MenuButton>
      
      <MenuButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        isActive={editor.isActive('italic')}
        title="Italic (Ctrl+I)"
      >
        <Italic className="w-4 h-4" />
      </MenuButton>
      
      <MenuButton
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        isActive={editor.isActive('underline')}
        title="Underline (Ctrl+U)"
      >
        <UnderlineIcon className="w-4 h-4" />
      </MenuButton>
      
      <MenuButton
        onClick={() => editor.chain().focus().toggleStrike().run()}
        isActive={editor.isActive('strike')}
        title="Strikethrough"
      >
        <Strikethrough className="w-4 h-4" />
      </MenuButton>
      
      <div className="w-px h-5 bg-brand-border mx-1" />
      
      <MenuButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        isActive={editor.isActive('bulletList')}
        title="Bullet List"
      >
        <List className="w-4 h-4" />
      </MenuButton>
      
      <MenuButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={editor.isActive('orderedList')}
        title="Numbered List"
      >
        <ListOrdered className="w-4 h-4" />
      </MenuButton>
      
      <div className="w-px h-5 bg-brand-border mx-1" />
      
      <MenuButton
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        isActive={editor.isActive('blockquote')}
        title="Quote"
      >
        <Quote className="w-4 h-4" />
      </MenuButton>
      
      <MenuButton
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        isActive={editor.isActive('codeBlock')}
        title="Code Block"
      >
        <Code className="w-4 h-4" />
      </MenuButton>
      
      <MenuButton
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        title="Horizontal Rule"
      >
        <Minus className="w-4 h-4" />
      </MenuButton>
      
      <div className="w-px h-5 bg-brand-border mx-1" />
      
      <MenuButton
        onClick={addLink}
        isActive={editor.isActive('link')}
        title="Add Link"
      >
        <LinkIcon className="w-4 h-4" />
      </MenuButton>
      
      <MenuButton
        onClick={handleImageClick}
        title="Insert Image"
      >
        <ImageIcon className="w-4 h-4" />
      </MenuButton>
      
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />
      
      <div className="w-px h-5 bg-brand-border mx-1" />
      
      <MenuButton
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        title="Undo (Ctrl+Z)"
      >
        <Undo className="w-4 h-4" />
      </MenuButton>
      
      <MenuButton
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        title="Redo (Ctrl+Shift+Z)"
      >
        <Redo className="w-4 h-4" />
      </MenuButton>
    </div>
  );
};

// Custom extension for handling paste and drop with image upload
const createImagePasteExtension = (onImageUpload) => {
  return Extension.create({
    name: 'imagePasteHandler',
    
    addProseMirrorPlugins() {
      const uploadImage = onImageUpload;
      
      return [
        new Plugin({
          key: new PluginKey('imagePasteHandler'),
          props: {
            handlePaste: (view, event) => {
              const items = event.clipboardData?.items;
              if (!items) return false;

              // Check for image files in clipboard
              for (let i = 0; i < items.length; i++) {
                const item = items[i];
                if (item.type.startsWith('image/')) {
                  event.preventDefault();
                  const file = item.getAsFile();
                  if (file) {
                    uploadImage(file).catch(err => {
                      console.error('Failed to upload pasted image:', err);
                    });
                    return true;
                  }
                }
              }
              
              // Let TipTap handle text paste normally
              return false;
            },
            
            handleDrop: (view, event) => {
              const files = event.dataTransfer?.files;
              if (!files || files.length === 0) return false;

              const file = files[0];
              if (!file.type.startsWith('image/')) return false;

              event.preventDefault();
              uploadImage(file).catch(err => {
                console.error('Failed to upload dropped image:', err);
              });
              return true;
            },
          },
        }),
      ];
    },
  });
};

const RichTextEditor = ({ 
  value, 
  onChange, 
  placeholder = 'Write your description here...',
  className = '',
  editable = true,
  minHeight = '150px'
}) => {
  const editorRef = useRef(null);
  
  const handleImageUpload = useCallback(async (file) => {
    if (!editorRef.current) return;
    
    try {
      // Upload to MinIO via API
      const response = await fileAPI.upload(file);
      const imageUrl = response.data.url;
      
      // Get full URL
      const fullUrl = imageUrl.startsWith('http') 
        ? imageUrl 
        : `${window.location.origin}${imageUrl}`;
      
      // Insert image into editor
      editorRef.current.chain().focus().setImage({ src: fullUrl, alt: file.name }).run();
      
      return fullUrl;
    } catch (error) {
      console.error('Failed to upload image:', error);
      throw error;
    }
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Underline,
      Image.configure({
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded-lg my-2',
        },
        allowBase64: true, // Allow base64 for immediate paste feedback
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-brand-primary underline cursor-pointer',
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
      createImagePasteExtension(handleImageUpload),
    ],
    content: value || '',
    editable,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // Convert empty editor to empty string
      const isEmpty = editor.isEmpty;
      onChange?.(isEmpty ? '' : html);
    },
    editorProps: {
      attributes: {
        class: 'ProseViewer prose prose-sm max-w-none focus:outline-none',
      },
    },
  });

  // Store editor reference
  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  // Update content when value changes externally
  useEffect(() => {
    if (editor && value !== undefined) {
      const currentContent = editor.getHTML();
      // Only update if content is actually different
      if (value !== currentContent && !(value === '' && editor.isEmpty)) {
        editor.commands.setContent(value || '');
      }
    }
  }, [value, editor]);

  return (
    <div className={`border border-brand-border rounded-lg overflow-hidden bg-white ${className}`}>
      {editable && (
        <MenuBar editor={editor} onImageUpload={handleImageUpload} />
      )}
      <EditorContent 
        editor={editor} 
        className="p-3"
        style={{ minHeight }}
      />
      <style>{`
        .ProseMirror {
          min-height: ${minHeight};
        }
        .ProseMirror p.is-editor-empty:first-child::before {
          color: #9CA3AF;
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }
        .ProseMirror:focus {
          outline: none;
        }
        .ProseMirror img {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
          margin: 8px 0;
        }
        .ProseMirror img.ProseMirror-selectednode {
          outline: 2px solid #2F6F63;
        }
        .ProseMirror blockquote {
          border-left: 3px solid #2F6F63;
          padding-left: 1rem;
          margin: 1rem 0;
          color: #6B7280;
        }
        .ProseMirror pre {
          background: #1F2937;
          color: #E5E7EB;
          padding: 1rem;
          border-radius: 8px;
          overflow-x: auto;
        }
        .ProseMirror code {
          background: #F3F4F6;
          padding: 0.2rem 0.4rem;
          border-radius: 4px;
          font-size: 0.875rem;
        }
        .ProseMirror pre code {
          background: transparent;
          padding: 0;
        }
        .ProseMirror ul, .ProseMirror ol {
          padding-left: 1.5rem;
          margin: 0.5rem 0;
        }
        .ProseMirror ul {
          list-style-type: disc;
        }
        .ProseMirror ol {
          list-style-type: decimal;
        }
        .ProseMirror h1 {
          font-size: 1.5rem;
          font-weight: 700;
          margin: 1rem 0 0.5rem;
        }
        .ProseMirror h2 {
          font-size: 1.25rem;
          font-weight: 600;
          margin: 0.75rem 0 0.5rem;
        }
        .ProseMirror h3 {
          font-size: 1.1rem;
          font-weight: 600;
          margin: 0.5rem 0 0.25rem;
        }
        .ProseMirror hr {
          border: none;
          border-top: 1px solid #E5E7EB;
          margin: 1rem 0;
        }
        .ProseMirror p {
          margin: 0.25rem 0;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
